import {
    Features,
    getFeatureFlagValue,
} from '@experiences/feature-flags';
import type { IPagination } from '@experiences/interfaces';
import urljoin from 'url-join';

import {
    axiosDelete,
    get,
    getFile,
    post,
    put,
} from '../utility/Requests';
import { getIdentityBaseRoute } from './IdentityServiceUtils';

const consentsBaseUrl = `/api/identity/Consent`;

interface ConsentContent {
    title: string;
    content: string;
    acceptRadioContent: string;
    declineRadioContent: string;
}

interface ConsentBase {
    recurrenceInDays: number | null;
    content?: ConsentContent;
    isActive: boolean;
    priority: number;
}

export interface Consent extends ConsentBase {
    id: string;
    partitionId: string;
    isDeleted?: boolean;
    creationTime: Date;
    lastModificationTime?: Date | null;
    deletionTime?: Date | null;

    createdBy?: string;
    userRecordCount?: number;
    title?: string; // Flattened title for search functionality
}

interface UserRecordsResponse {
    totalCount: number;
    results: ConsentUserRecord[];
}

export interface ConsentUserRecord {
    id: string; // this is user ID but I map it to ID for the grid
    partitionId: number;
    consentId: string;
    createdOn: string;
    userName: string;
    userEmail: string;
    lastUpdateTime: string;
    consentAccepted: string;
}

interface CreateConsentPayload extends ConsentBase {}
interface UpdateConsentPayload extends ConsentBase {
    userReconsentNeeded: boolean;
}

const getConsentsBaseUrl = (partitionGlobalId: string, consentId: string = '') => {
    // host mode is false until we support On-Premise
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    const baseUrl = EnableMultipleOrgConsents ? urljoin(getIdentityBaseRoute(partitionGlobalId, false), 'api', 'PartitionConsent') : consentsBaseUrl;
    return urljoin(baseUrl, partitionGlobalId, consentId);
};

export async function getConsents({ partitionGlobalId }: { partitionGlobalId: string }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    const consent = await get<Consent>(getConsentsBaseUrl(partitionGlobalId), { ignoreBasePath: EnableMultipleOrgConsents });
    return consent?.content ? [ consent ] : [];
}

export async function getAllConsents({ partitionGlobalId }: { partitionGlobalId: string }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    const consents = await get<Consent[]>(urljoin(getConsentsBaseUrl(partitionGlobalId), 'all'), { ignoreBasePath: EnableMultipleOrgConsents });
    return consents.map((consent) => {
        try {
            return {
                ...consent,
                content: JSON.parse(consent.content as any || '{}'), // Content is returned as a string so we need to parse it first for the title
            };
        } catch {
            return consent;
        }
    });
}

export async function getConsentUserRecords({
    consentId, partitionGlobalId, pagination,
}: { consentId: string; partitionGlobalId: string; pagination: IPagination }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    const response = await get<UserRecordsResponse>(urljoin(getConsentsBaseUrl(partitionGlobalId, consentId), 'UserConsents'), {
        pagination,
        ignoreBasePath: EnableMultipleOrgConsents,
    });
    if (!EnableMultipleOrgConsents) {
        return response;
    }

    const userRecords = response.results.map((record: any) => {
        record.id = record.userId;
        record.userEmail = `${record.userName} (${record.userEmail})`;
        return record;
    });
    return {
        totalCount: response.totalCount,
        results: userRecords,
    };
}

export async function exportUserRecords({
    consentId, partitionGlobalId,
}: { consentId: string; partitionGlobalId: string }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    return await getFile(urljoin(getConsentsBaseUrl(partitionGlobalId, consentId), 'Download'), { ignoreBasePath: EnableMultipleOrgConsents });
}

export async function createNewConsent({
    partitionGlobalId, body,
}: { partitionGlobalId: string; body: CreateConsentPayload }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    return await post<CreateConsentPayload>(getConsentsBaseUrl(partitionGlobalId), {
        body: EnableMultipleOrgConsents ? {
            ...body,
            content: JSON.stringify(body.content),
        } : body,
        ignoreBasePath: EnableMultipleOrgConsents,
    });
}

export async function getConsentById({
    partitionGlobalId, consentId,
}: { partitionGlobalId: string; consentId: string }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    const response = await get<Consent>(getConsentsBaseUrl(partitionGlobalId, consentId), { ignoreBasePath: EnableMultipleOrgConsents });
    try {
        return {
            ...response,
            content: JSON.parse(response.content as any || '{}'), // Content is returned as a string so we need to parse it first for the title
        };
    } catch {
        return undefined;
    }
}

export async function updateConsent({
    partitionGlobalId, consentId, body,
}: { partitionGlobalId: string; consentId: string; body: UpdateConsentPayload }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    return await put<UpdateConsentPayload>(getConsentsBaseUrl(partitionGlobalId, consentId), {
        body: EnableMultipleOrgConsents ? {
            ...body,
            content: JSON.stringify(body.content),
        } : body,
        ignoreBasePath: EnableMultipleOrgConsents,
    });
}

export async function deleteConsent({
    partitionGlobalId, consentId,
}: { partitionGlobalId: string; consentId: string }) {
    const EnableMultipleOrgConsents = getFeatureFlagValue(Features.EnableMultipleOrgConsents.name);
    return await axiosDelete(getConsentsBaseUrl(partitionGlobalId, consentId), { ignoreBasePath: EnableMultipleOrgConsents });
}
